import { PanoramaButton as Button, type PanoramaButtonProps } from 'shared-ui/components'
import { createVariants } from 'shared-ui/lib'
import { isNonNil } from 'utils'

export const inputStyleClass = 'border border-gray-300 rounded-md py-2 px-2 outline-green-600'

const inputWithBottomMarginStyleClass = `mb-4 ${inputStyleClass}`

const inputClasses = createVariants({
  base: inputWithBottomMarginStyleClass,
  variants: {
    hasError: { true: 'ring ring-red-500' }
  }
})

const Input: React.FC<React.ComponentPropsWithRef<'input'> & { hasError?: boolean }> = ({
  hasError,
  className,
  ...props
}) => <input className={inputClasses({ hasError, className })} {...props} />

const textAreaClasses = createVariants({ base: inputWithBottomMarginStyleClass })

const TextArea: React.FC<React.ComponentPropsWithRef<'textarea'>> = ({ className, ...props }) => (
  <textarea className={textAreaClasses({ className })} {...props} />
)

const headerClasses = createVariants({ base: 'pan-title2' })

const Header: React.FC<React.ComponentPropsWithRef<'h2'>> = ({ className, ...props }) => (
  <h2 className={headerClasses({ className })} {...props} />
)

const errorMessageClasses = createVariants({
  base: 'flex flex-row justify-center items-center my-4 p-2 bg-red-100 rounded-sm text-red-600 text-sm border border-red-600 shadow'
})

export const ErrorMessage: React.FC<React.ComponentPropsWithRef<'div'>> = ({
  className,
  ...props
}) => <div className={errorMessageClasses({ className })} {...props} />

interface LabelWithErrorProps {
  htmlFor: string
  errorMsg?: string
  children: React.ReactNode
}

const LabelWithErrors: React.FC<LabelWithErrorProps> = ({ children, errorMsg, htmlFor }) => (
  <div className="flex flex-row">
    <label className="pan-label mb-2" htmlFor={htmlFor}>
      {children}
    </label>
    {isNonNil(errorMsg) && (
      <span className="ml-auto text-sm text-red-500" role="alert">
        {errorMsg}
      </span>
    )}
  </div>
)

const cancelButtonClasses = createVariants({
  base: 'my-2 flex w-full justify-center rounded-md border border-transparent px-4 py-2 text-sm font-medium text-gray-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-green-500 focus-visible:ring-offset-2'
})

const CancelButton: React.FC<React.ComponentPropsWithRef<'button'>> = ({ className, ...props }) => (
  <button className={cancelButtonClasses({ className })} {...props} />
)

type SubmissionErrorProps = { children?: React.ReactNode }

const SubmissionError: React.FC<SubmissionErrorProps> = ({ children }) =>
  isNonNil(children) && <ErrorMessage role="alert">{children}</ErrorMessage>

interface FormProps {
  className?: string
  onSubmit?: (e: React.FormEvent) => void
  onChange?: (e: React.FormEvent) => void
  children: React.ReactNode
}

const submitButtonClasses = createVariants({ base: 'w-full' })

const SubmitButton: React.FC<PanoramaButtonProps> = ({ className, ...props }) => (
  <Button className={submitButtonClasses({ className })} {...props} />
)

type Form = React.FC<FormProps> & {
  Header: typeof Header
  Label: typeof LabelWithErrors
  Input: typeof Input
  TextArea: typeof TextArea
  SubmitButton: typeof SubmitButton
  SubmissionError: typeof SubmissionError
  CancelButton: typeof CancelButton
}

const Form: Form = ({ children, className, onSubmit, onChange }) => (
  <form className={`flex flex-col ${className ?? ''}`} onSubmit={onSubmit} onChange={onChange}>
    {children}
  </form>
)

Form.Input = Input
Form.TextArea = TextArea
Form.Header = Header
Form.Label = LabelWithErrors
Form.SubmitButton = SubmitButton
Form.SubmissionError = SubmissionError
Form.CancelButton = CancelButton

export default Form
